import React, {useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {pluralDays} from "../utils/plural";

interface AdherenceSignal {
    severity: string;
    text: string;
}

interface Adherence {
    configured: boolean;
    archetype: string | null;
    targetSize: number | null;
    actualSize: number;
    sizeDelta: number | null;
    sizeStatus: string;
    deadStockThresholdDays: number;
    /** null — истории носки ещё мало; ноль рядом с «Залежавшихся вещей: 1» путал (Q6). */
    staleItemsCount: number | null;
    /** Флаг вместо null: число остаётся 0 ради iOS, а «не посчитано» приходит отдельно. */
    staleItemsNotComputed?: boolean;
    adherenceScore: number | null;
    signals: AdherenceSignal[];
}

interface Props {
    ownerId: string | null;
}

const SIZE_STATUS_LABEL: Record<string, string> = {
    OVER: 'больше цели',
    UNDER: 'меньше цели',
    ON_TARGET: 'в цели',
    UNKNOWN: 'цель не задана',
};

const SEVERITY_CLASS: Record<string, string> = {
    OK: 'text-emerald-600 dark:text-emerald-400',
    INFO: 'text-gray-500 dark:text-gray-400',
    WARN: 'text-amber-600 dark:text-amber-400',
};

function scoreColor(score: number): string {
    if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 50) return 'text-amber-600 dark:text-amber-400';
    return 'text-rose-600 dark:text-rose-400';
}

const StrategyAdherenceCard: React.FC<Props> = ({ownerId}) => {
    const [data, setData] = useState<Adherence | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setFailed(false);
            try {
                const api = await createAxiosInstance();
                const params = ownerId ? {ownerId} : {};
                const res = await api.get<Adherence>('/strategy/adherence', {params});
                if (!cancelled) setData(res.data);
            } catch (e) {
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ownerId]);

    if (loading || failed || !data || !data.configured) {
        // Карточка соответствия показывается только когда есть стратегия и данные.
        return null;
    }

    return (
        <section
            data-testid="adherence-card"
            className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 flex flex-col sm:flex-row gap-4"
        >
            <div className="flex flex-col items-center justify-center sm:w-32 shrink-0">
                <span className="text-xs text-gray-500 dark:text-gray-400">Соответствие</span>
                <span className={`text-4xl font-bold ${data.adherenceScore != null ? scoreColor(data.adherenceScore) : ''}`}
                      data-testid="adherence-score">
                    {data.adherenceScore != null ? `${data.adherenceScore}` : '—'}
                </span>
                <span className="text-xs text-gray-400">из 100</span>
            </div>

            <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap gap-2 text-sm">
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-ink-700 text-gray-700 dark:text-gray-200">
                        Размер: {data.actualSize}
                        {data.targetSize != null && ` / ${data.targetSize}`}
                        {' '}({SIZE_STATUS_LABEL[data.sizeStatus] || data.sizeStatus})
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-ink-700 text-gray-700 dark:text-gray-200"
                          data-testid="adherence-stale">
                        {(data.staleItemsNotComputed || data.staleItemsCount == null)
                            ? `Залежалось: — · история копится ещё ${pluralDays(data.deadStockThresholdDays)}`
                            : `Залежалось: ${data.staleItemsCount} (порог ${pluralDays(data.deadStockThresholdDays)})`}
                    </span>
                </div>
                <ul className="space-y-1" data-testid="adherence-signals">
                    {data.signals.map((s, idx) => (
                        <li key={idx} className={`text-sm ${SEVERITY_CLASS[s.severity] || ''}`}>
                            {s.text}
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
};

export default StrategyAdherenceCard;
